import { Component, HostBinding, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LSKey } from '@app/enums/localStorageKey.enum';
import { AppointmentRequest } from '@app/models/appointmentRequest.model';
import { Client } from '@app/models/client.model';
import { Service } from '@app/models/service.model';
import { Site } from '@app/models/site.model';
import { SiteBookingSettings } from '@app/models/siteBookingSettings.model';
import { DateTimeOfDay } from '@app/models/syncAppointmentRequest.model';
import { ContentService } from '@app/services/content.service';
import { FeatureFlagService, FeatureKey } from '@app/services/feature-flag.service';
import {
    APPOINTMENT_REQUEST,
    CUSTOMER_ID,
    LocalStorageService,
    OLD_APPOINTMENT_REQUEST,
    SITE,
    SITE_BOOKING_SETTINGS,
} from '@app/services/local-storage.service';
import { LocaleService } from '@app/services/locale.service';
import { UserAccountService } from '@app/services/user-account.service';
import { getBookingDate, getBookingTime } from '@app/shared/helpers/extensions';
import { AnimationOptions } from 'ngx-lottie';

@Component({
    selector: 'app-confirmation',
    templateUrl: './confirmation.component.html',
    styleUrls: ['./confirmation.component.css'],
    standalone: false,
})
export class ConfirmationComponent implements OnInit {
    @HostBinding('class') classes = 'confirmation-component-container';
    ticketID: number;
    appointmentRequest!: AppointmentRequest;
    formattedDate: string = '';
    formattedTime: string = '';
    employeeName: string = '';
    serviceName: string = '';
    clientName: string = '';
    changeRequestText = 'To change your request call:';
    phoneNumberExists = false;
    serviceDuration = 0;
    servicePrice = 0;
    isServicePriceAndDurationFeatureActive = false;
    shouldShowServicePrice = false;
    currencyCode: string = '';
    siteSettings!: SiteBookingSettings;
    customerID!: string;
    isRescheduleAppointment = false;
    isApprovalRequired = false;
    userLoggedIn = false;
    supportsPets = false;
    isRequestAppointmentOnly = false;
    dateTimeOfDayList: DateTimeOfDay[] = [];

    constructor(
        private localStorageService: LocalStorageService,
        private router: Router,
        private localeService: LocaleService,
        private featureFlagService: FeatureFlagService,
        private userAccountService: UserAccountService,
        public contentService: ContentService
    ) {
        const navigation = this.router.getCurrentNavigation();
        this.isServicePriceAndDurationFeatureActive = this.featureFlagService.getFeatureFlag(FeatureKey.ShowPriceAndDuration);

        if (navigation) {
            this.ticketID = navigation.extras.state?.ticketID;
        } else {
            throw new Error('Navigation is null');
        }
    }
    public animationOptions: AnimationOptions = {
        path: '../../../assets/appointment-submitted.json',
        loop: false,
    };
    async ngOnInit() {
        this.getDataFromLocalStorage();
        this.userLoggedIn = this.userAccountService.getCurrentUser() !== undefined;
        // this.setupMockData();
        this.employeeName = this.appointmentRequest.employee[0].name;
        const client = this.appointmentRequest?.client;
        this.clientName = `${client?.firstName ?? ''} ${client?.lastName ?? ''}`.trim();
        if (this.clientName.length === 0) {
            this.clientName = client?.email ?? '';
        }
        await this.localeService.loadLocaleFile(this.siteSettings.customerCultureCode, true);
        this.currencyCode = this.localeService.getCurrencyCode() || 'USD';
        this.isRescheduleAppointment = !!this.appointmentRequest?.rescheduleGuid;
        this.isApprovalRequired = this.siteSettings.requireApproval ?? false;
        this.dateTimeOfDayList = this.appointmentRequest?.dateTimeOfDayList ?? [];
        this.isRequestAppointmentOnly =
            this.featureFlagService.getFeatureFlag(FeatureKey.DSBRequestOnly) && this.dateTimeOfDayList.length > 0;
        this.shouldShowServicePrice =
            this.isServicePriceAndDurationFeatureActive &&
            (this.siteSettings.showPricesWithServices || this.siteSettings.showPricesWithEmployees);

        this.localStorageService.remove(APPOINTMENT_REQUEST);
        this.localStorageService.remove(OLD_APPOINTMENT_REQUEST);
        if (this.siteSettings.businessPhoneNumber) {
            this.phoneNumberExists = true;
        } else {
            this.changeRequestText = this.changeRequestText.replace(':', ' the business.');
        }
    }

    get getBookingDate() {
        return getBookingDate(this.appointmentRequest.datetime);
    }

    get getBookingTime() {
        return getBookingTime(this.appointmentRequest.datetime);
    }

    requestNewAppointment() {
        let client: Client | undefined = undefined;
        if (this.userAccountService.getCurrentUser() !== undefined) {
            client = this.appointmentRequest?.client;
        }
        if (client) {
            this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Client, client);
        }

        this.contentService.sendData('new appointment');
        void this.router.navigate(['/booking/service'], {
            queryParams: {
                DSID: this.customerID,
            },
        });
    }

    onClickViewProfile(): void {
        const site = this.localStorageService.get(SITE) as Site;
        let url = site?.bookingURL + '/User#overview';
        if (!url.startsWith('http')) {
            url = 'https://' + url;
        }
        window.location.href = url;
    }

    getServicesPrice(services: Service[]) {
        return services.reduce((total, service) => {
            const price = this.isRequestOnlyAndFirstAvailableEmployee()
                ? (service.lowestPrice ?? service.price ?? 0)
                : (this.appointmentRequest.employee[0].employeeServicePriceList?.find((ep) => ep?.serviceID === service.id)?.price ?? 0);
            return total + (price ?? 0);
        }, 0);
    }

    getServicesDuration(services: Service[]) {
        return services.reduce((total, service) => {
            let duration = 0;
            if (this.isRequestOnlyAndFirstAvailableEmployee()) {
                duration = service.hasDurationException ? (service.longestDuration ?? service.duration ?? 0) : (service.duration ?? 0);
            } else {
                const durationException = this.appointmentRequest.employee[0].durationExceptionList?.find(
                    (de) => de.serviceID === service.id
                );
                duration = durationException ? durationException.duration : service.duration;
            }
            return total + duration;
        }, 0);
    }

    getServicePrice(service: Service) {
        return this.getServicesPrice([service]);
    }

    getServiceDuration(service: Service) {
        return this.getServicesDuration([service]);
    }

    getGroupedDateTimeOfDays(): { date: string; labels: string }[] {
        const grouped = new Map<string, string[]>();
        for (const entry of this.dateTimeOfDayList) {
            const dateKey = entry.date;
            if (!grouped.has(dateKey)) {
                grouped.set(dateKey, []);
            }
            grouped.get(dateKey)!.push(entry.timeOfDay);
        }
        return Array.from(grouped.entries()).map(([date, timeOfDays]) => ({
            date,
            labels: timeOfDays.join(', '),
        }));
    }

    private getDataFromLocalStorage() {
        this.appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        this.siteSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;
        this.customerID = this.localStorageService.getString(CUSTOMER_ID);
        const site = this.localStorageService.get(SITE) as Site;
        this.supportsPets = site?.supportsPets;
    }

    public isRequestOnlyAndFirstAvailableEmployee(): boolean {
        return (
            this.isRequestAppointmentOnly &&
            (this.appointmentRequest?.employee?.[0]?.id === '0' || this.appointmentRequest?.employee?.[0]?.firstAvailable === true)
        );
    }

    public anyServiceHasDurationException(): boolean {
        return this.appointmentRequest?.service?.some((service) => service.hasDurationException === true) ?? false;
    }

    public anyServiceHasPriceException(): boolean {
        return this.appointmentRequest?.service?.some((service) => service.hasPriceException === true) ?? false;
    }
}
